/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from "react";
import { IEvidenceSearchCard } from "@interfaces/Components";
import * as ProgramUtils from "@utils/programUtils";
import * as EvidenceUtils from "@utils/evidenceUtils";
import {
  AllProgressCheckStatuses,
  CompetenceActivityClass,
  EvidenceCustomTag,
  IEvidence,
  IUser,
  ProgressCheck,
} from "@interfaces/Interfaces";
import { format } from "date-fns";
import * as StringUtils from "@utils/stringUtils";
import { DOT_SEPARATOR } from "@utils/stringUtils";
import EvidenceDateHeader from "./EvidenceDateHeader";
import { EventRegister } from "react-native-event-listeners";
import { useProgramData } from "@hooks/data/useProgramData";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { evidenceAtom, progressCheckDataAtom, userAtom } from "@state/State";
import _ from "lodash";
import EvidenceCard from "./EvidenceCard";
import { Icon_Audit_Plus, Icon_Audit_Tick } from "@assets/images";
import classNames from "classnames";
import { userRolesSelector } from "@state/UserState";
import { useProgramCycles } from "@hooks/programs/useProgramCycles";
import { useToggleAuditTag } from "@hooks/evidence/useToggleAuditTag";
import * as SPAPRPUtils from "@utils/programs/SPAPRPUtils";

const EvidenceSearchCard: React.FC<IEvidenceSearchCard> = React.memo((props) => {
  const evidenceJSON: any =
    props.evidence.evidenceJSON && typeof props.evidence.evidenceJSON === "string"
      ? JSON.parse(props.evidence.evidenceJSON)
      : props.evidence.evidenceJSON;

  const { optimisticAuditTag, toggleAuditTag } = useToggleAuditTag(props.evidence);
  const { currentProgramCycle } = useProgramCycles(evidenceJSON?.programInfo?.programID);
  const { userPrograms } = useProgramData();
  const allUserRoles = useRecoilValue(userRolesSelector);

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const allProgressCheckData = useRecoilValueLoadable<AllProgressCheckStatuses[] | null>(progressCheckDataAtom);
  const allEvidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);

  const program = userPrograms.find((program) => program.ID === evidenceJSON?.programInfo.programID);
  const taskingType: string | undefined = evidenceJSON?.["Tasking type or training"];

  const progressCheck = useMemo(
    () => evidenceJSON?.programInfo?.progressCheckID && ProgramUtils.getProgressCheckForEvidence(evidenceJSON, program),
    [evidenceJSON, program]
  );
  const progressCheckCompetence: CompetenceActivityClass | null = useMemo(
    () =>
      evidenceJSON?.programInfo?.progressCheckCompetenceID &&
      ProgramUtils.getProgressCheckCompetenceActivityForEvidence(evidenceJSON, program),
    [evidenceJSON, program]
  );

  const programEvidenceType = evidenceJSON
    ? ProgramUtils.getEvidenceType({
        evidenceJSON: evidenceJSON,
        usesAdditionalEvidenceDefinitions: program?.UsesAdditionalEvidenceDefinitions ?? false,
        evidenceDefinitions: program?.["Evidence Definitions"] ?? [],
        formEvidenceDefinitions: program?.["WPBA Forms"]?.flatMap((form) => form["Evidence Definitions"] ?? []) ?? [],
      })
    : null;

  const programName = program?.Title || "";
  const progressCheckName: string | null = progressCheck?.Name ?? evidenceJSON?.programInfo?.progressCheck ?? null;
  const progressCheckCompetenceName: string | null =
    progressCheckCompetence?.Name ?? evidenceJSON?.programInfo?.progressCheckCompetence ?? null;
  const competenceName = evidenceJSON?.programInfo?.competence || "";
  const title = evidenceJSON?.Title ?? props.evidence?.title ?? "";

  const hasSearchText = props.searchText.trim().length > 0;

  const evidenceTypeString = programEvidenceType
    ? ProgramUtils.getEvidenceTypeString(program, programEvidenceType)
    : null;

  const customTagsKey = ProgramUtils.getEvidenceCustomTagsKey(props.evidence);
  const skill = evidenceJSON && EvidenceUtils.getEvidenceSkillKey(props.evidence);
  const skillType = evidenceJSON && EvidenceUtils.getEvidenceSkillTypeKey(props.evidence);
  const witnessSimulationSkill = EvidenceUtils.getWitnessSimulationSkill(props.evidence, program);
  const skillSection = EvidenceUtils.getSkillSectionForEvidence(props.evidence, program);
  const witnessEvidenceApproved = evidenceJSON && evidenceJSON.programInfo.witnessSimulationApproval;

  const getLabel = () => {
    if (program && ProgramUtils.showStandardsLabelForEvidenceCard(program.ID)) {
      try {
        const standards: string[] = [];
        const parsedEvidence = props.evidence.evidenceJSON;
        const comps: any[] = Array.from(parsedEvidence.Comps);
        comps.forEach((comp) => standards.push(comp.Name));
        return standards.join(", ");
      } catch (error) {
        return "";
      }
    } else {
      return "";
    }
  };

  const label = getLabel();

  const customTags: EvidenceCustomTag[] = customTagsKey ? evidenceJSON?.[customTagsKey] : props.evidence.customTags;

  const searchInTitle = hasSearchText && title?.toLowerCase().includes(props.searchText.toLowerCase());
  const searchInActivityType =
    hasSearchText &&
    props.evidence.activity !== null &&
    props.evidence.activity?.type.toLowerCase().includes(props.searchText.toLowerCase());
  const searchInDate =
    hasSearchText &&
    format(new Date(props.evidence.date), "d MMM yyyy").toLowerCase().includes(props.searchText.toLowerCase());
  const searchInProgram = hasSearchText && programName?.toLowerCase().includes(props.searchText.toLowerCase());
  const searchInProgressCheck =
    hasSearchText && progressCheckName?.toLowerCase().includes(props.searchText.toLowerCase());
  const searchInProgressCheckCompetence =
    hasSearchText && progressCheckCompetenceName?.toLowerCase().includes(props.searchText.toLowerCase());
  const searchInCompetence = hasSearchText && competenceName?.toLowerCase().includes(props.searchText.toLowerCase());
  const searchInTaskingType = hasSearchText && taskingType?.toLowerCase().includes(props.searchText.toLowerCase());
  const searchInEvidenceType =
    hasSearchText && evidenceTypeString?.toLowerCase().includes(props.searchText.toLowerCase());
  const searchInSkill = hasSearchText && skill?.toLowerCase().includes(props.searchText.toLowerCase());
  const searchInSkillType = hasSearchText && skillType?.toLowerCase().includes(props.searchText.toLowerCase());

  let splitTitle: string[] = [];
  let splitActivityType: string[] = [];
  let splitDate: string[] = [];
  let splitProgram: string[] = [];
  let splitProgressCheck: string[] = [];
  let splitCompetence: string[] = [];
  let splitTaskingType: string[] = [];
  let splitProgressCheckCompetence: string[] = [];
  let splitEvidenceType: string[] = [];
  let splitSkill: string[] = [];
  let splitSkillType: string[] = [];

  const regex = new RegExp(`(${StringUtils.escapeRegExp(props.searchText)})`, "gi");

  if (searchInTitle) {
    splitTitle = title?.split(regex) || [];
    splitTitle = splitTitle.filter((item) => item.length > 0);
  }

  if (searchInActivityType) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    splitActivityType = props.evidence.activity?.type.split(regex)!;
    splitActivityType = splitActivityType.filter((item) => item.length > 0);
  }

  if (searchInDate) {
    splitDate = format(new Date(props.evidence.date), "d MMM yyyy").split(regex);
    splitDate = splitDate.filter((item) => item.length > 0);
  }

  if (searchInProgram) {
    splitProgram = programName?.split(regex) || [];
    splitProgram = splitProgram.filter((item) => item.length > 0);
  }

  if (searchInProgressCheck) {
    splitProgressCheck = progressCheckName?.split(regex) || [];
    splitProgressCheck = splitProgressCheck.filter((item) => item.length > 0);
  }

  if (searchInProgressCheckCompetence) {
    splitProgressCheckCompetence = progressCheckCompetenceName?.split(regex) || [];
    splitProgressCheckCompetence = splitProgressCheckCompetence.filter((item) => item.length > 0);
  }

  if (searchInCompetence) {
    splitCompetence = competenceName?.split(regex) || [];
    splitCompetence = splitCompetence.filter((item) => item.length > 0);
  }

  if (searchInTaskingType) {
    splitTaskingType = taskingType?.split(regex) || [];
    splitTaskingType = splitTaskingType.filter((item) => item.length > 0);
  }

  if (searchInSkill) {
    splitSkill = skill?.split(regex) || [];
    splitSkill = splitSkill.filter((item) => item.length > 0);
  }

  if (searchInSkillType) {
    splitSkillType = skillType?.split(regex) || [];
    splitSkillType = splitSkillType.filter((item) => item.length > 0);
  }

  if (searchInSkill) {
    splitSkill = skill?.split(regex) || [];
    splitSkill = splitSkill.filter((item) => item.length > 0);
  }

  if (searchInSkillType) {
    splitSkillType = skillType?.split(regex) || [];
    splitSkillType = splitSkillType.filter((item) => item.length > 0);
  }

  if (searchInEvidenceType) {
    splitEvidenceType = evidenceTypeString?.split(regex) || [];
    splitEvidenceType = splitEvidenceType.filter((item) => item.length > 0);
  }

  const onHoldReset = evidenceJSON?.OnHoldReset === 1;

  let progressCheckLocked;

  if (program) {
    progressCheckLocked =
      ProgramUtils.isNQPProgram(program.ID) &&
      ProgramUtils.isProgramCompleteNQP(
        program.ProgressChecks,
        allProgressCheckData.contents,
        allEvidence.contents,
        program?.ID
      );
  }

  const generalAttachments = props.evidence.attachments ?? [];
  const programAttachmentsLength = EvidenceUtils.getProgramEvidenceAttachmentsLength(props.evidence);

  const attachmentsLength = generalAttachments.length + programAttachmentsLength;

  let expired;
  let pending;

  if (EvidenceUtils.isWPBAFormEvidenceType(programEvidenceType)) {
    expired = SPAPRPUtils.isWPBAFormEvidenceExpired(props.evidence);
    pending = SPAPRPUtils.isWPBAFormEvidencePending(props.evidence);
  } else {
    const approvalPending =
      evidenceJSON?.programInfo?.approvalState === "Pending" || evidenceJSON?.programInfo?.pending === true;
    if (approvalPending) {
      const expiryDate = evidenceJSON?.programInfo?.expiryDate;
      if (expiryDate && new Date(expiryDate) < new Date()) {
        expired = true;
      } else {
        pending = pending || true;
      }
    }
  }

  const approvalPending = evidenceJSON?.programInfo?.approvalState === "Pending";
  if (approvalPending) {
    const expiryDate = evidenceJSON?.programInfo?.expiryDate;
    if (expiryDate && new Date(expiryDate) < new Date()) {
      expired = true;
    } else {
      pending = pending || true;
    }
  }

  const editEvidence = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();

    const _evidence = _.pickBy(_.cloneDeep(props.evidence), _.identity);

    if (_evidence.evidenceJSON) {
      const parsedEvidence = props.evidence.evidenceJSON;
      const program = userPrograms.find((program) => program.ID === parsedEvidence.programInfo.programID);

      const evidenceDefinitions = program?.["Evidence Definitions"] || [];
      const formEvidenceDefinitions =
        program?.["WPBA Forms"]?.flatMap((form) => form["Evidence Definitions"] ?? []) ?? [];
      const mandatoryStandards = ProgramUtils.getMandatoryStandards(user.contents, program!);
      const optionalStandards = ProgramUtils.getOptionalStandards(user.contents, program!);
      const skills = ProgramUtils.getSkillsForRole(user.contents, program!);

      const competenceName = parsedEvidence.programInfo?.competence;
      const competence = program?.CompetenceActivity?.find(
        (item: any): item is CompetenceActivityClass => item.Name === competenceName
      );

      const progressCheckID = parsedEvidence.programInfo?.progressCheckID;
      const progressCheckCompetenceID = parsedEvidence.programInfo?.progressCheckCompetenceID;

      const progressCheck = program?.ProgressChecks?.find(
        (item: any): item is ProgressCheck => item.ID === progressCheckID
      );

      const progressCheckCompetence = progressCheck?.["Competence/Activity"]?.find(
        (item: any) => item.ID === progressCheckCompetenceID
      );

      let data: object = {};
      const usesAdditionalEvidenceDefinitions = program?.UsesAdditionalEvidenceDefinitions;

      const evidenceType = ProgramUtils.getEvidenceType({
        evidenceJSON: parsedEvidence,
        usesAdditionalEvidenceDefinitions: usesAdditionalEvidenceDefinitions ?? false,
        evidenceDefinitions,
        formEvidenceDefinitions,
      });

      if (evidenceType === "skills") {
        const programRoles = allUserRoles.find((role) => role.programID === program?.ID);
        let userRoles: string[] = [];

        if (programRoles) {
          userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
        }

        const learningObjectives =
          program && ProgramUtils.getLearningOutcomesFromRole(program?.ID)
            ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0])
            : progressCheckCompetence?.["Learning Objective"] ?? [];

        data = {
          program: program,
          ...(program?.CompetenceActivity &&
            program?.CompetenceActivity.length > 0 && { competences: program?.CompetenceActivity }),
          ...(evidenceDefinitions.length > 0 &&
            usesAdditionalEvidenceDefinitions && {
              evidenceDefinitions: evidenceDefinitions[0],
            }),
          competence: competence,
          evidence: _evidence,
          ...(progressCheckCompetence && {
            progressCompetence: progressCheckCompetence,
          }),
          progressCheck: progressCheck,
          ...(program?.ProgressChecks &&
            program?.ProgressChecks.length > 0 &&
            !usesAdditionalEvidenceDefinitions && { progressChecks: program.ProgressChecks }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(skills.length > 0 && { skills }),
          evidenceJSON: _evidence.evidenceJSON,
          skill: null,
          id: _evidence.id,
          learningOutcomes: learningObjectives,
          progressCheckCompetence: progressCheckCompetence,
          currentProgressCheckId: progressCheck?.ID ?? "",
          disableProgressCheckDropdown: progressCheck !== undefined,
          disableProgressCheckCompetenceDropdown: progressCheckCompetence !== undefined,
        };
      } else if (evidenceType === "witness-simulation") {
        const evidenceDefinition = evidenceDefinitions.find(
          (item) => item.EvidenceSelectionType === "witness-simulation"
        );
        const preselectedSkillID = parsedEvidence.programInfo?.witnessSimulationSkillID;
        const preselectedSkill = skills.find((item) => item.ID === preselectedSkillID);

        data = {
          ...data,
          program: program,
          evidenceDefinitions: evidenceDefinition,
          evidence: _evidence,
          ...(skills.length > 0 && { skills }),
          ...(preselectedSkill && { preselectedSkill }),
          evidenceJSON: _evidence.evidenceJSON,
          id: _evidence.id,
        };
      } else if (evidenceType === "capabilities") {
        const programRoles = allUserRoles.find((role) => role.programID === program?.ID);
        let userRoles: string[] = [];

        if (programRoles) {
          userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
        }

        const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "capabilities");
        const learningObjectives =
          program && ProgramUtils.getLearningOutcomesFromRole(program?.ID)
            ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0])
            : [];

        data = {
          ...data,
          program: program,
          ...(evidenceDefinitions.length > 0 &&
            usesAdditionalEvidenceDefinitions && {
              evidenceDefinitions: evidenceDefinition,
            }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(learningObjectives.length > 0 && { learningObjectives }),
          evidenceJSON: parsedEvidence,
          id: _evidence.id,
        };
      } else if (evidenceType === "medical-imaging") {
        const programRoles = allUserRoles.find((role) => role.programID === program?.ID);
        let userRoles: string[] = [];

        if (programRoles) {
          userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
        }

        const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "medical-imaging");
        const learningObjectives = program ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]) : [];

        data = {
          ...data,
          program: program,
          ...(evidenceDefinitions.length > 0 &&
            usesAdditionalEvidenceDefinitions && {
              evidenceDefinitions: evidenceDefinition,
            }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(learningObjectives.length > 0 && { learningObjectives }),
          ...(skills.length > 0 && { skills }),
          evidenceJSON: parsedEvidence,
          id: _evidence.id,
        };
      } else if (evidenceType === "wpba-supervision-form") {
        const programRoles = allUserRoles.find((role) => role.programID === program?.ID);
        let userRoles: string[] = [];

        if (programRoles) {
          userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
        }
        const evidenceDefinition = formEvidenceDefinitions.find(
          (item) => item.ID === parsedEvidence.programInfo.programEvidenceDefinitionID
        );

        const learningObjectives = program ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]) : [];

        const wpbaForm = program?.["WPBA Forms"]?.find((form) =>
          form["Evidence Definitions"]?.find(
            (item) => item.ID === parsedEvidence.programInfo.programEvidenceDefinitionID
          )
        );

        data = {
          ...data,
          program: program,
          ...(evidenceDefinitions.length > 0 &&
            usesAdditionalEvidenceDefinitions && {
              evidenceDefinitions: evidenceDefinition,
            }),
          ...(learningObjectives.length > 0 && { learningObjectives }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(skills.length > 0 && { skills }),
          evidenceJSON: parsedEvidence,
          id: _evidence.id,
          wpbaForm: wpbaForm,
        };
      } else if (evidenceType === "ropp-workbook") {
        const programRoles = allUserRoles.find((role) => role.programID === program?.ID);
        let userRoles: string[] = [];

        if (programRoles) {
          userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
        }

        const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "ropp-workbook");
        const learningObjectives = program ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]) : [];

        data = {
          ...data,
          program: program,
          ...(evidenceDefinitions.length > 0 &&
            usesAdditionalEvidenceDefinitions && {
              evidenceDefinitions: evidenceDefinition,
            }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(learningObjectives.length > 0 && { learningObjectives }),
          evidenceJSON: parsedEvidence,
          id: _evidence.id,
        };
      } else if (evidenceType === "cap-workbook") {
        const programRoles = allUserRoles.find((role) => role.programID === program?.ID);
        let userRoles: string[] = [];

        if (programRoles) {
          userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
        }

        const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "cap-workbook");
        const learningObjectives = program ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]) : [];

        data = {
          ...data,
          program: program,
          ...(evidenceDefinitions.length > 0 &&
            usesAdditionalEvidenceDefinitions && {
              evidenceDefinitions: evidenceDefinition,
            }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(learningObjectives.length > 0 && { learningObjectives }),
          evidenceJSON: parsedEvidence,
          id: _evidence.id,
        };
      } else {
        const _evidenceDefinition = usesAdditionalEvidenceDefinitions
          ? evidenceDefinitions.find((d) => d.ID === parsedEvidence.programInfo?.programEvidenceDefinitionID)
          : evidenceDefinitions[0];

        const skillID = parsedEvidence.programInfo?.skillID;
        const _autoFilledSkill = skillID ? skills.find((s) => s.ID === skillID) ?? null : null;
        const existingApprovalState = parsedEvidence.programInfo?.approvalState;

        data = {
          program: program,
          ...(program?.CompetenceActivity &&
            program?.CompetenceActivity.length > 0 && { competences: program?.CompetenceActivity }),
          ...(_evidenceDefinition && { evidenceDefinitions: _evidenceDefinition }),
          competence: competence,
          evidence: _evidence,
          ...(progressCheckCompetence && {
            progressCompetence: progressCheckCompetence,
          }),
          progressCheck: progressCheck,
          ...(program?.ProgressChecks &&
            program?.ProgressChecks.length > 0 && { progressChecks: program.ProgressChecks }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(skills.length > 0 && { skills }),
          evidenceJSON: _evidence.evidenceJSON,
          skill: null,
          id: _evidence.id,
          learningOutcomes: progressCheckCompetence?.["Learning Objective"] ?? [],
          progressCheckCompetence: progressCheckCompetence,
          currentProgressCheckId: progressCheck?.ID ?? "",
          disableProgressCheckDropdown: progressCheck !== undefined,
          disableProgressCheckCompetenceDropdown: progressCheckCompetence !== undefined,
          ...(_autoFilledSkill && { autoFilledSkill: _autoFilledSkill }),
          ...(existingApprovalState && { approvalState: existingApprovalState }),
        };
      }

      EventRegister.emit("evidence/add-program-evidence", data);
    } else {
      EventRegister.emit("evidence/evidence-card-clicked", _evidence);
    }
  };

  const canEdit = EvidenceUtils.canEdit(
    props.evidence,
    userPrograms,
    allProgressCheckData,
    progressCheckLocked,
    allEvidence.contents,
    currentProgramCycle
  );

  return (
    <div>
      {props.showHeader && (
        <div className="flex justify-center">
          <EvidenceDateHeader date={new Date(props.evidence.date)} />
        </div>
      )}
      <div className="timelineBlock">
        <EvidenceCard onClick={props.buttonPressed}>
          <div className="flex w-full flex-col py-3.5 pl-safe-area-l pr-safe-area-r">
            <div className="flex justify-between">
              <div>
                <div className="evidenceCardDateContainer flex flex-row flex-wrap gap-2">
                  {props.evidence.evidenceJSON?.programInfo.programCycle && (
                    <div
                      className={classNames("flex h-4 items-center justify-center rounded-xl bg-grey-30 px-2", {
                        "!bg-green-background":
                          props.evidence.evidenceJSON.programInfo.programCycle === currentProgramCycle,
                      })}
                    >
                      <div
                        className={classNames("text-12px font-semibold text-grey-60", {
                          "!text-green-text":
                            props.evidence.evidenceJSON.programInfo.programCycle === currentProgramCycle,
                        })}
                      >{`Cycle ${props.evidence.evidenceJSON.programInfo.programCycle}`}</div>
                    </div>
                  )}
                  {expired && (
                    <span className="flex h-4 items-center justify-center rounded-xl bg-cta-red px-2">
                      <span className="text-12px font-semibold text-white">{"Expired"}</span>
                    </span>
                  )}
                  {pending && (
                    <span className="flex h-4 items-center justify-center rounded-xl bg-orange-30 px-2">
                      <span className="text-12px font-semibold text-orange-text">{"Pending"}</span>
                    </span>
                  )}
                  {evidenceJSON?.programInfo?.approvalState === "Approved" && (
                    <span className="flex h-4 items-center justify-center rounded-xl bg-green-background px-2">
                      <span className="text-12px font-semibold text-green-text">{"Approved"}</span>
                    </span>
                  )}
                  {evidenceJSON?.programInfo?.approvalState === "Rejected" && (
                    <span className="flex h-4 items-center justify-center rounded-xl bg-cta-red px-2">
                      <span className="text-12px font-semibold text-white">{"Rejected"}</span>
                    </span>
                  )}
                  {props.evidence.private && (
                    <div className="flex h-4 items-center justify-center rounded-xl bg-grey-30 px-2">
                      <div className="text-12px font-semibold text-grey-60">{"Private"}</div>
                    </div>
                  )}
                  {witnessEvidenceApproved && (
                    <div className="flex h-4 items-center justify-center rounded-xl bg-green-background px-2">
                      <div className="text-12px font-semibold text-green-text">{"Approved"}</div>
                    </div>
                  )}
                  {props.evidence.evidenceJSON?.programInfo.locked && (
                    <>
                      {EvidenceUtils.isWPBAFormEvidenceType(programEvidenceType) ? (
                        <div className="flex h-4 items-center justify-center rounded-xl bg-green-background px-2">
                          <div className="text-12px font-semibold text-green-text">{"Completed"}</div>
                        </div>
                      ) : (
                        <div className="flex h-4 items-center justify-center rounded-xl bg-grey-30 px-2">
                          <div className="text-12px font-semibold text-grey-60">{"Locked"}</div>
                        </div>
                      )}
                    </>
                  )}
                  {props.evidence.draft && (
                    <div className="flex h-4 items-center justify-center rounded-xl bg-orange-30 px-2">
                      <div className="text-12px font-semibold text-orange-text">{"Draft"}</div>
                    </div>
                  )}
                  <span className="flex flex-row flex-wrap items-center">
                    <span className="evidenceCardDate">
                      {splitDate.length > 0 ? (
                        <>
                          {splitDate.map((split: string, _index: number) => {
                            const searchTerm = split.toLowerCase() === props.searchText.toLowerCase();

                            if (searchTerm) {
                              return (
                                <span
                                  key={`evidence-date-${split}-${_index}`}
                                  className="timelineBlockSearchHighlight"
                                >
                                  {split}
                                </span>
                              );
                            }

                            return <span key={`evidence-date-${split}-${_index}`}>{split}</span>;
                          })}
                        </>
                      ) : (
                        <>{format(new Date(props.evidence.date), "d MMM yyyy") || ""}</>
                      )}
                    </span>
                    {props.evidence.activity?.type && (
                      <span className="evidenceCardActivity">
                        {DOT_SEPARATOR}
                        {splitActivityType.length > 0 ? (
                          <>
                            {splitActivityType.map((split: string, _index: number) => {
                              const searchTerm = split.toLowerCase() === props.searchText.toLowerCase();

                              if (searchTerm) {
                                return (
                                  <span
                                    key={`evidence-activity-${split}-${_index}`}
                                    className="timelineBlockSearchHighlight"
                                  >
                                    {split}
                                  </span>
                                );
                              }

                              return <span key={`evidence-activity-${split}-${_index}`}>{split}</span>;
                            })}
                          </>
                        ) : (
                          <>{props.evidence.activity?.type}</>
                        )}
                      </span>
                    )}
                    {programName.length > 0 && (
                      <span className="evidenceCardActivity">
                        {DOT_SEPARATOR}
                        {splitProgram.length > 0 ? (
                          <>
                            {splitProgram.map((split: string, _index: number) => {
                              const searchTerm = split.toLowerCase() === props.searchText.toLowerCase();

                              if (searchTerm) {
                                return (
                                  <span
                                    key={`evidence-activity-${split}-${_index}`}
                                    className="timelineBlockSearchHighlight"
                                  >
                                    {split}
                                  </span>
                                );
                              }

                              return <span key={`evidence-activity-${split}-${_index}`}>{split}</span>;
                            })}
                          </>
                        ) : (
                          <>{programName}</>
                        )}
                      </span>
                    )}
                    {evidenceTypeString && evidenceTypeString?.length > 0 && (
                      <span className="evidenceCardActivity">
                        {DOT_SEPARATOR}
                        {splitEvidenceType.length > 0 ? (
                          <>
                            {splitEvidenceType.map((split: string, _index: number) => {
                              const searchTerm = split.toLowerCase() === props.searchText.toLowerCase();

                              if (searchTerm) {
                                return (
                                  <span
                                    key={`evidence-activity-${split}-${_index}`}
                                    className="timelineBlockSearchHighlight"
                                  >
                                    {split}
                                  </span>
                                );
                              }

                              return <span key={`evidence-activity-${split}-${_index}`}>{split}</span>;
                            })}
                          </>
                        ) : (
                          <>{evidenceTypeString}</>
                        )}
                      </span>
                    )}
                    {competenceName.length > 0 && (
                      <span className="evidenceCardActivity">
                        {DOT_SEPARATOR}
                        {splitCompetence.length > 0 ? (
                          <>
                            {splitCompetence.map((split: string, _index: number) => {
                              const searchTerm = split.toLowerCase() === props.searchText.toLowerCase();

                              if (searchTerm) {
                                return (
                                  <span
                                    key={`evidence-activity-${split}-${_index}`}
                                    className="timelineBlockSearchHighlight"
                                  >
                                    {split}
                                  </span>
                                );
                              }

                              return <span key={`evidence-activity-${split}-${_index}`}>{split}</span>;
                            })}
                          </>
                        ) : (
                          <>{competenceName}</>
                        )}
                      </span>
                    )}
                    {progressCheckName && progressCheckName?.length > 0 && (
                      <span className="evidenceCardActivity">
                        {DOT_SEPARATOR}
                        {splitProgressCheck.length > 0 ? (
                          <>
                            {splitProgressCheck.map((split: string, _index: number) => {
                              const searchTerm = split.toLowerCase() === props.searchText.toLowerCase();

                              if (searchTerm) {
                                return (
                                  <span
                                    key={`evidence-activity-${split}-${_index}`}
                                    className="timelineBlockSearchHighlight"
                                  >
                                    {split}
                                  </span>
                                );
                              }

                              return <span key={`evidence-activity-${split}-${_index}`}>{split}</span>;
                            })}
                          </>
                        ) : (
                          <>{progressCheckName}</>
                        )}
                      </span>
                    )}
                    {skill && skill.length > 0 && (
                      <span className="evidenceCardActivity">
                        {DOT_SEPARATOR}
                        {splitSkill.length > 0 ? (
                          <>
                            {splitSkill.map((split: string, _index: number) => {
                              const searchTerm = split.toLowerCase() === props.searchText.toLowerCase();

                              if (searchTerm) {
                                return (
                                  <span
                                    key={`evidence-activity-${split}-${_index}`}
                                    className="timelineBlockSearchHighlight"
                                  >
                                    {split}
                                  </span>
                                );
                              }

                              return <span key={`evidence-skill-${split}-${_index}`}>{split}</span>;
                            })}
                          </>
                        ) : (
                          <>{skill}</>
                        )}
                      </span>
                    )}
                    {skillType && skillType.length > 0 && (
                      <span className="evidenceCardActivity">
                        {DOT_SEPARATOR}
                        {splitSkillType.length > 0 ? (
                          <>
                            {splitSkillType.map((split: string, _index: number) => {
                              const searchTerm = split.toLowerCase() === props.searchText.toLowerCase();

                              if (searchTerm) {
                                return (
                                  <span
                                    key={`evidence-activity-${split}-${_index}`}
                                    className="timelineBlockSearchHighlight"
                                  >
                                    {split}
                                  </span>
                                );
                              }

                              return <span key={`evidence-skill-type-${split}-${_index}`}>{split}</span>;
                            })}
                          </>
                        ) : (
                          <>{skill}</>
                        )}
                      </span>
                    )}
                    {witnessSimulationSkill && witnessSimulationSkill?.Name?.length > 0 && (
                      <span className="flex flex-row">
                        <div className="text-acc-12px font-acc-600 leading-[1.33] tracking-[-0.1px] text-portfolio-blue">
                          {DOT_SEPARATOR}
                          {witnessSimulationSkill.Name}
                        </div>
                      </span>
                    )}
                    {skillSection && skillSection.length > 0 && (
                      <span className="flex flex-row">
                        <div className="text-acc-12px font-acc-600 leading-[1.33] tracking-[-0.1px] text-portfolio-blue">
                          {DOT_SEPARATOR}
                          {skillSection}
                        </div>
                      </span>
                    )}
                    {progressCheckCompetenceName && progressCheckCompetenceName?.length > 0 && (
                      <span className="evidenceCardActivity">
                        {DOT_SEPARATOR}
                        {splitProgressCheckCompetence.length > 0 ? (
                          <>
                            {splitProgressCheckCompetence.map((split: string, _index: number) => {
                              const searchTerm = split.toLowerCase() === props.searchText.toLowerCase();

                              if (searchTerm) {
                                return (
                                  <span
                                    key={`evidence-activity-${split}-${_index}`}
                                    className="timelineBlockSearchHighlight"
                                  >
                                    {split}
                                  </span>
                                );
                              }

                              return <span key={`evidence-activity-${split}-${_index}`}>{split}</span>;
                            })}
                          </>
                        ) : (
                          <>{progressCheckCompetenceName}</>
                        )}
                      </span>
                    )}
                    {onHoldReset && (
                      <span className="flex flex-row">
                        <div className="whitespace-break-spaces text-acc-12px font-acc-500 leading-[1.33] tracking-[-0.1px] text-cta-red">
                          {DOT_SEPARATOR}
                          {"Submission Reset"}
                        </div>
                      </span>
                    )}
                    {attachmentsLength > 0 && (
                      <span className="flex flex-row">
                        <div className="text-acc-12px font-acc-600 leading-[1.33] tracking-[-0.1px] text-portfolio-blue">
                          {DOT_SEPARATOR}
                          {`${attachmentsLength} attachment${attachmentsLength > 1 ? "s" : ""}`}
                        </div>
                      </span>
                    )}
                  </span>
                </div>
                <div
                  className={classNames("evidenceCardTitle", {
                    "mr-4": canEdit,
                  })}
                >
                  {splitTitle.length > 0 ? (
                    <>
                      {splitTitle.map((split: string, _index: number) => {
                        const searchTerm = split.toLowerCase() === props.searchText.toLowerCase();

                        if (searchTerm) {
                          return (
                            <span
                              key={`evidence-title-${split}-${_index}`}
                              className="timelineBlockSearchHighlight"
                            >
                              {split}
                            </span>
                          );
                        }

                        return <span key={`evidence-title-${split}-${_index}`}>{split}</span>;
                      })}
                    </>
                  ) : (
                    <>{title}</>
                  )}
                </div>
                <div className="mb-2">
                  <span className="evidenceTagsContainer">
                    {taskingType && (
                      <>
                        {splitTaskingType.length > 0 ? (
                          <span className="evidenceCustomTags">
                            {splitTaskingType.map((split: string, _index: number) => {
                              const searchTerm = split.toLowerCase() === props.searchText.toLowerCase();

                              if (searchTerm) {
                                return (
                                  <span
                                    key={`evidence-tasking-type-${split}-${_index}`}
                                    className="timelineBlockSearchHighlight"
                                  >
                                    {split}
                                  </span>
                                );
                              }

                              return <span key={`evidence-tasking-type-${split}-${_index}`}>{split}</span>;
                            })}
                          </span>
                        ) : (
                          <span className="evidenceCustomTags">{taskingType}</span>
                        )}
                      </>
                    )}
                    {customTags && customTags.length > 0 && (
                      <span className="evidenceCustomTags">
                        {customTags.map((tag: EvidenceCustomTag) => tag.tag).join(", ")}
                      </span>
                    )}
                    {label && label.length > 0 && (
                      <span className="mb-2 overflow-visible text-acc-12px font-acc-500 leading-default tracking-[-0.1px] text-grey-60">
                        {label}
                      </span>
                    )}
                  </span>
                </div>
                <button
                  onClick={(e) => toggleAuditTag(e)}
                  className={classNames(
                    "self-center rounded-[4px] bg-grey-20 px-1.5 py-1 text-acc-13px font-semibold text-cta-blue",
                    {
                      "!bg-green-200 text-neutral-800": optimisticAuditTag,
                    }
                  )}
                >
                  {optimisticAuditTag ? (
                    <div className="flex flex-row items-center justify-center">
                      <img src={Icon_Audit_Tick} />
                      <div className="ml-1">{"Added to audit"}</div>
                    </div>
                  ) : (
                    <div className="flex flex-row items-center justify-center">
                      <img
                        src={Icon_Audit_Plus}
                        className="mr-2"
                      />
                      <div>{"Add to audit"}</div>
                    </div>
                  )}
                </button>
              </div>
              {canEdit && (
                <button
                  onClick={(e) => editEvidence(e)}
                  className="self-center rounded-[4px] bg-grey-20 px-1.5 py-1 text-acc-13px font-semibold text-cta-blue"
                >
                  {"Edit"}
                </button>
              )}
            </div>
          </div>
        </EvidenceCard>
      </div>
    </div>
  );
});

EvidenceSearchCard.displayName = "EvidenceSearchCard";

export default EvidenceSearchCard;
